import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import {
  BookOpen, Search, Plus, RefreshCw, Trash2, Pencil, ToggleLeft,
  ToggleRight, X, Loader2, Cloud, Book, FileText, ShoppingBag,
  Tag as TagIcon, Sparkles,
} from "lucide-react";

// Knowledge Base admin console — lists every doc (auto + manual), lets
// admins add or edit manual docs, toggle any doc on/off, and trigger a
// full auto-sync (from modules/worksheets/downloads → kb_documents).
//
// Auto docs are marked read-only for content — only their `is_active`
// flag can be toggled. Editing an auto doc happens in the source page
// (Modules Console, Worksheets Console, etc.).
export default function KnowledgeConsole() {
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | auto | manual
  const [prefixFilter, setPrefixFilter] = useState("all");
  const [syncing, setSyncing] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // manual doc being edited

  const load = () =>
    api
      .get("/admin/kb/documents")
      .then((r) => setDocs(r.data || []))
      .catch((e) => toast.error(formatError(e)));

  useEffect(() => { load(); }, []);

  const prefixes = useMemo(() => {
    const set = new Set();
    for (const d of docs) {
      const p = (d.source_ref || "").split(":")[0];
      if (p) set.add(p);
    }
    return Array.from(set).sort();
  }, [docs]);

  const filtered = useMemo(() => {
    const qLower = q.trim().toLowerCase();
    return docs.filter((d) => {
      if (typeFilter !== "all" && d.source_type !== typeFilter) return false;
      if (prefixFilter !== "all") {
        const p = (d.source_ref || "").split(":")[0];
        if (p !== prefixFilter) return false;
      }
      if (!qLower) return true;
      const hay = [d.title, d.content, (d.tags || []).join(" "), d.source_ref]
        .join(" ").toLowerCase();
      return hay.includes(qLower);
    });
  }, [docs, q, typeFilter, prefixFilter]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/kb/sync");
      const counts = r.data.synced || {};
      const total = Object.values(counts).reduce((a, b) => a + b, 0);
      toast.success(`Synced ${total} docs`);
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSyncing(false); }
  };

  const pushToElevenLabs = async () => {
    if (!window.confirm(
      "Re-upload every active doc to ElevenLabs and update the agent config?\n\n" +
      "This can take 30-90 seconds depending on how many docs are active."
    )) return;
    setPushing(true);
    try {
      const r = await api.post("/admin/kb/push-to-elevenlabs");
      toast.success(`Pushed ${r.data.docs_synced} docs to agent ${r.data.agent_id?.slice(0, 24)}…`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setPushing(false); }
  };

  const toggle = async (doc) => {
    try {
      await api.post(`/admin/kb/documents/${doc.id}/toggle`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"?`)) return;
    try {
      await api.delete(`/admin/kb/documents/${doc.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const counts = useMemo(() => {
    const active = docs.filter((d) => d.is_active).length;
    const manual = docs.filter((d) => d.source_type === "manual").length;
    return { total: docs.length, active, manual };
  }, [docs]);

  return (
    <div data-testid="admin-kb-console" className="min-w-0">
      {/* Header stats + Sync button */}
      <div className="mb-6 flex flex-wrap items-baseline gap-4 min-w-0">
        <div>
          <h2 className="font-display text-2xl flex items-center gap-2">
            <BookOpen className="w-5 h-5" /> Knowledge Base
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Everything the AI voice agent (and Morgan) can talk about.
            Auto-synced from modules, worksheets, and downloads — plus anything you add manually below.
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <span className="text-xs text-muted-foreground" data-testid="admin-kb-counts">
            <span className="font-semibold text-foreground">{counts.total}</span> total ·
            <span className="font-semibold text-foreground"> {counts.active}</span> active ·
            <span className="font-semibold text-foreground"> {counts.manual}</span> manual
          </span>
          <button
            onClick={syncNow}
            disabled={syncing}
            data-testid="admin-kb-sync-now"
            className="btn-outline inline-flex items-center gap-1.5 text-sm disabled:opacity-60"
          >
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {syncing ? "Syncing…" : "Sync now"}
          </button>
          <button
            onClick={pushToElevenLabs}
            disabled={pushing}
            data-testid="admin-kb-push-elevenlabs"
            className="btn-outline inline-flex items-center gap-1.5 text-sm disabled:opacity-60"
          >
            {pushing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Cloud className="w-3.5 h-3.5" />}
            {pushing ? "Pushing…" : "Push to ElevenLabs"}
          </button>
          <button
            onClick={() => setCreating(true)}
            data-testid="admin-kb-add-manual"
            className="btn-primary text-sm inline-flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Add manual doc
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search titles, tags, content…"
            data-testid="admin-kb-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-shell-border text-sm focus:outline-none focus:border-primary/40"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          data-testid="admin-kb-filter-type"
          className="rounded-lg border border-shell-border px-3 py-2 text-sm bg-white"
        >
          <option value="all">All types</option>
          <option value="auto">Auto-synced</option>
          <option value="manual">Manual</option>
        </select>
        <select
          value={prefixFilter}
          onChange={(e) => setPrefixFilter(e.target.value)}
          data-testid="admin-kb-filter-prefix"
          className="rounded-lg border border-shell-border px-3 py-2 text-sm bg-white"
        >
          <option value="all">All sources</option>
          {prefixes.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>

      {/* Docs list — horizontal scroll on narrow viewports so every column
          (esp. the rightmost actions) stays reachable. `min-w-[1150px]`
          keeps columns from collapsing into unreadable widths and reliably
          triggers a horizontal scrollbar at 1200-1280px viewports too. */}
      <div className="admin-scroll-x rounded-xl border border-shell-border bg-white min-w-0" data-testid="admin-kb-list">
        <table className="w-full min-w-[1150px] text-sm">
          <thead className="bg-muted/60 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Title</th>
              <th className="text-left px-4 py-2 font-semibold">Source</th>
              <th className="text-left px-4 py-2 font-semibold">Tags</th>
              <th className="text-left px-4 py-2 font-semibold">Updated</th>
              <th className="text-left px-4 py-2 font-semibold">State</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                No docs match your filter. Try "Sync now" if the list is empty.
              </td></tr>
            ) : filtered.map((d) => (
              <KbRow key={d.id} doc={d} onToggle={() => toggle(d)}
                     onEdit={() => setEditing(d)} onDelete={() => remove(d)} />
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <ManualDocModal
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
      {editing && (
        <ManualDocModal
          doc={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ── Row ────────────────────────────────────────────────────────────────
function KbRow({ doc, onToggle, onEdit, onDelete }) {
  const prefix = (doc.source_ref || "").split(":")[0] || "manual";
  const Icon = prefixIcon(prefix);
  const isManual = doc.source_type === "manual";
  return (
    <tr className="border-t border-shell-border hover:bg-muted/30" data-testid={`admin-kb-row-${doc.id}`}>
      <td className="px-4 py-3 max-w-[380px]">
        <div className="flex items-start gap-2 min-w-0">
          <Icon className="w-4 h-4 text-shell-side shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="font-semibold truncate" data-testid={`admin-kb-row-title-${doc.id}`}>{doc.title}</p>
            <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">
              {(doc.content || "").slice(0, 160)}{(doc.content || "").length > 160 ? "…" : ""}
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-muted-foreground text-xs">
        <p className="font-semibold uppercase tracking-widest">{doc.source_type}</p>
        <p className="mt-0.5 truncate max-w-[180px]" title={doc.source_ref}>{doc.source_ref}</p>
      </td>
      <td className="px-4 py-3 max-w-[200px]">
        <div className="flex flex-wrap gap-1">
          {(doc.tags || []).slice(0, 4).map((t) => (
            <span key={t} className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
              {t}
            </span>
          ))}
          {(doc.tags || []).length > 4 && (
            <span className="text-[10px] text-muted-foreground">+{(doc.tags || []).length - 4}</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-muted-foreground text-xs">
        {doc.updated_at ? new Date(doc.updated_at).toLocaleDateString() : "—"}
      </td>
      <td className="px-4 py-3">
        <button
          onClick={onToggle}
          data-testid={`admin-kb-toggle-${doc.id}`}
          className={"inline-flex items-center gap-1 text-xs font-semibold " +
            (doc.is_active ? "text-primary" : "text-muted-foreground")}
        >
          {doc.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
          {doc.is_active ? "Active" : "Off"}
        </button>
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        {isManual ? (
          <>
            <button
              onClick={onEdit}
              data-testid={`admin-kb-edit-${doc.id}`}
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mr-3"
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
            <button
              onClick={onDelete}
              data-testid={`admin-kb-delete-${doc.id}`}
              className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1"
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </>
        ) : (
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Read-only
          </span>
        )}
      </td>
    </tr>
  );
}

function prefixIcon(prefix) {
  switch (prefix) {
    case "module":    return Book;
    case "worksheet": return FileText;
    case "download":  return ShoppingBag;
    case "deal":      return TagIcon;
    case "manual":    return Sparkles;
    default:          return Cloud;
  }
}

// ── Manual doc modal (create OR edit) ──────────────────────────────────
function ManualDocModal({ doc, onClose, onSaved }) {
  const isEdit = !!doc;
  const [title, setTitle] = useState(doc?.title || "");
  const [content, setContent] = useState(doc?.content || "");
  const [tags, setTags] = useState((doc?.tags || []).join(", "));
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    const body = {
      title: title.trim(),
      content: content.trim(),
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/admin/kb/documents/${doc.id}`, body);
        toast.success("Doc updated");
      } else {
        await api.post("/admin/kb/documents", body);
        toast.success("Doc added");
      }
      onSaved?.();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-shell-side/60 backdrop-blur-sm grid place-items-center p-4"
         data-testid="admin-kb-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-shell-border flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              {isEdit ? "Edit manual doc" : "Add manual doc"}
            </p>
            <h3 className="font-display text-xl mt-1">
              {isEdit ? title : "New knowledge base entry"}
            </h3>
          </div>
          <button type="button" onClick={onClose}
            data-testid="admin-kb-modal-close"
            className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-4 grid gap-4">
          <label className="grid gap-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Title</span>
            <input
              autoFocus={!isEdit}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Founder Discount Program Terms"
              data-testid="admin-kb-modal-title"
              className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
            />
          </label>
          <label className="grid gap-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Content</span>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={10}
              placeholder="Paste in the full body the agent should know verbatim. Markdown OK."
              data-testid="admin-kb-modal-content"
              className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40 font-mono resize-y"
            />
          </label>
          <label className="grid gap-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
              Tags (comma-separated)
            </span>
            <input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="policy, refund, pricing"
              data-testid="admin-kb-modal-tags"
              className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
            />
          </label>
          <p className="text-[11px] text-muted-foreground">
            Manual docs get uploaded to ElevenLabs the next time the backend restarts or you hit "Sync now" from an admin script.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-shell-border flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-kb-modal-cancel"
            className="text-xs font-semibold text-muted-foreground hover:text-foreground border border-shell-border rounded-full px-4 py-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-kb-modal-save"
            className="btn-primary text-sm disabled:opacity-60 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            {isEdit ? "Save changes" : "Add doc"}
          </button>
        </div>
      </form>
    </div>
  );
}
